// Web Audio API tone generator for ICT event alerts
// Each event has a distinct tone (frequency + waveform + duration).
// Supports per-event mute + per-timeframe overrides.

let ctx = null;
let enabled = true;
let perEvent = {};
let perTimeframe = {};   // { "1m": { FVG: false }, "5m": {...} }  — override map; missing key = inherit perEvent

function ensureCtx() {
  if (!ctx) {
    try { ctx = new (window.AudioContext || window.webkitAudioContext)(); }
    catch { ctx = null; }
  }
  if (ctx && ctx.state === "suspended") ctx.resume();
  return ctx;
}

function tone({ freq = 440, dur = 0.18, type = "sine", gain = 0.15, attack = 0.01, release = 0.08 }) {
  const ac = ensureCtx();
  if (!ac || !enabled) return;
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, ac.currentTime);
  g.gain.setValueAtTime(0, ac.currentTime);
  g.gain.linearRampToValueAtTime(gain, ac.currentTime + attack);
  g.gain.linearRampToValueAtTime(0, ac.currentTime + dur + release);
  osc.connect(g).connect(ac.destination);
  osc.start();
  osc.stop(ac.currentTime + dur + release + 0.05);
}

function chord(freqs, opts = {}) {
  freqs.forEach((f, i) => setTimeout(() => tone({ ...opts, freq: f }), i * 70));
}

// Pre-loaded HTML audio sample for order-fill confirmation.
// V2.6.8 — if the user owns "Custom execution sound" AND has uploaded a
// custom MP3 (stored as data-URL in localStorage), play that instead.
let orderFillAudio = null;
let lastCustomKey = null;
function playOrderFillSample() {
  try {
    const custom = localStorage.getItem("ict.customExecSound") || "";
    const useCustom = !!custom && localStorage.getItem("ict.customExecSound.enabled") !== "0";
    const key = useCustom ? `c:${custom.length}` : "default";
    if (!orderFillAudio || key !== lastCustomKey) {
      orderFillAudio = new Audio(useCustom ? custom : "/sounds/order_filled.mp3");
      orderFillAudio.preload = "auto";
      orderFillAudio.volume = 0.75;
      lastCustomKey = key;
    }
    orderFillAudio.currentTime = 0;
    const p = orderFillAudio.play();
    if (p && p.catch) p.catch(() => {});
  } catch {}
}

export const SOUNDS = {
  FVG:        () => tone({ freq: 880,  dur: 0.12, type: "triangle", gain: 0.10 }),
  IFVG:       () => chord([1046, 784],  { dur: 0.14, type: "triangle", gain: 0.10 }),
  BOS:        () => tone({ freq: 660,  dur: 0.22, type: "square",  gain: 0.08 }),
  MSS:        () => chord([523, 784, 1046], { dur: 0.16, type: "sawtooth", gain: 0.08 }),
  SWEEP_BSL:  () => chord([1320, 880],  { dur: 0.16, type: "sine",     gain: 0.10 }),
  SWEEP_SSL:  () => chord([392, 587],   { dur: 0.16, type: "sine",     gain: 0.10 }),
  SMT:        () => chord([740, 988, 740], { dur: 0.14, type: "triangle", gain: 0.09 }),
  SESSION:    () => tone({ freq: 440,  dur: 0.30, type: "sine",     gain: 0.07 }),
  PREMIUM:    () => tone({ freq: 988,  dur: 0.18, type: "triangle", gain: 0.08 }),
  DISCOUNT:   () => tone({ freq: 494,  dur: 0.18, type: "triangle", gain: 0.08 }),
  ORDER_FILL: () => playOrderFillSample(),
  PROFIT:     () => chord([659, 784, 988, 1318], { dur: 0.14, type: "sine", gain: 0.10 }),
  LOSS:       () => chord([330, 247, 196], { dur: 0.18, type: "sawtooth", gain: 0.08 }),
};

export function play(name, timeframe) {
  if (!enabled) return;
  // Don't fire alerts when tab is hidden — prevents the "tab-switch resume" sound burst.
  if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
  if (perEvent[name] === false) return;  // per-event opted out
  // Per-timeframe override: undefined → inherit; false → mute; true → play
  if (timeframe && perTimeframe[timeframe] && perTimeframe[timeframe][name] === false) return;
  const fn = SOUNDS[name];
  if (fn) fn();
}

export function setEnabled(v) { enabled = !!v; }
export function setPerEvent(map) { perEvent = map || {}; }
export function setPerTimeframe(map) { perTimeframe = map || {}; }
export function isEnabled() { return enabled; }
export function unlock() { ensureCtx(); }
